import Category from "@components/rent/category";
import Product from "@components/rent/product";

import { useState } from "react";
import {
  AtSymbolIcon,
  PhoneIcon,
  LocationMarkerIcon,
} from "@heroicons/react/solid";
import Image from "next/image";
import PageNavigation from "@components/navigation/pageNavigation";
import PageFooter from "@components/navigation/pageFooter";
import { useTranslation } from "next-i18next";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";

export default function Home({ categories, products, baseUrl }) {
  let [currentProducts, setCurrentProducts] = useState(null);
  let [activeCategory, setActiveCategory] = useState(null);
  const { t } = useTranslation(["common", "rent"]);

  function getCurrentProducts() {
    if (!currentProducts) {
      setCurrentProducts(products.data);

      return currentProducts;
    }

    return currentProducts;
  }

  function getProductsInCategory(id) {
    console.log(products.data);
    return products.data.filter(
      (product) => product.attributes.category?.data?.id === id
    );
  }

  function updateActiveCategory(id) {
    if (id === activeCategory) {
      setActiveCategory(null);
      setCurrentProducts(products.data);
      return;
    }

    const productsActive = getProductsInCategory(id);

    setActiveCategory(id);
    setCurrentProducts(productsActive);
  }

  return (
    <>
      <PageNavigation />
      <header>
        <div className="relative bg-black">
          <Image
            priority
            objectFit="cover"
            layout="fill"
            src="/assets/hero-image.jpeg"
            alt="prenájom stavebných strojov, bager, kamion, preprava"
            className="relative z-0 block opacity-25"
          />
          <main className="relative z-10 text-white !py-20 c-container">
            <h1 className="text-4xl text-transparent md:text-5xl md:leading-[4rem] bg-gradient-to-r from-default to-teal-600 bg-clip-text">
              {t("rent:header.title")}
            </h1>
            <p className="mt-4 md:w-1/2">{t("rent:header.description")}</p>
            <article className="mt-6">
              <a href="mailto:dominik.bako@lptex.sk" className="flex items-center mt-4">
                <AtSymbolIcon className="block w-5 h-5 text-default" />
                <p className="ml-2">dominik.bako@lptex.sk</p>
              </a>
              <a href="tel:+421911576777" className="flex items-center mt-4">
                <PhoneIcon className="block w-5 h-5 text-default" />
                <p className="ml-2">+421 911 576 777</p>
              </a>
              <a
                href="https://goo.gl/maps/wZMEUCoxisKFUF7i7"
                target="_blank"
                rel="noreferrer"
                className="flex items-center mt-4 space-x-2"
              >
                <LocationMarkerIcon className="block w-5 h-5 text-default" />
                <p className="ml-2">Košice</p>
              </a>
            </article>
          </main>
        </div>
      </header>

      <main className="c-container">
        <article className="flex flex-col md:flex-row md:flex-wrap">
          {categories?.data
            .sort((a, b) => a.attributes.priority - b.attributes.priority)
            .map((category) => {
              return (
                <Category
                  key={"category-" + category.id}
                  Name={category.attributes.Name}
                  Icon={
                    category?.attributes?.svg?.data
                      ? baseUrl + category.attributes.svg.data?.attributes?.url
                      : null
                  }
                  Click={() => {
                    updateActiveCategory(category.id);
                  }}
                  Active={activeCategory === category.id}
                />
              );
            })}
        </article>
        <article className="grid grid-cols-1 gap-6 mt-10 md:grid-cols-2 xl:grid-cols-3">
          {getCurrentProducts()?.map((product) => {
            return (
              <Product
                key={`product-${product.id}`}
                Name={product.attributes.Name}
                photos={product.attributes.Images}
                description={product.attributes.Description}
                specSheet={
                  product?.attributes.TechnicalSheet.data
                    ? baseUrl +
                      product.attributes.TechnicalSheet.data?.attributes?.url
                    : null
                }
                baseUrl={baseUrl}
                translations={[
                  t("rent:technicalSpecs"),
                  t("rent:downloadDatasheet"),
                  t("close"),
                ]}
              />
            );
          })}
        </article>
      </main>

      <PageFooter />
    </>
  );
}

export async function getStaticProps({ locale }) {
  const baseUrl = process.env.STRAPI_URL;

  try {
    const responseCategories = await fetch(
      `${baseUrl}/api/categories?locale=${locale}&populate=*`,
      {
        headers: {
          Authorization: `Bearer ${process.env.STRAPI_KEY}`,
        },
      }
    );

    const responseProducts = await fetch(
      `${baseUrl}/api/products?locale=${locale}&populate=*`,
      {
        headers: {
          Authorization: `Bearer ${process.env.STRAPI_KEY}`,
        },
      }
    );

    const categories = await responseCategories.json();
    const products = await responseProducts.json();

    return {
      props: {
        categories,
        products,
        baseUrl,
        ...(await serverSideTranslations(locale, ["common", "rent"])),
      },
      revalidate: 60,
    };
  } catch (error) {
    console.log(error);

    return null;
  }
}
